/**
 * The month on Timer til lønn (D23, backend/services/payMonth.js): open, exported (when, by whom) or
 * closed, and a warning when the lines no longer match the last export. The administrator closes
 * the month with "Lukk måned" and reopens it with a reason; while it is closed the server refuses
 * every change to approved hours in it.
 *
 * The month, its lock and its log are the ACTIVE company's (the server reads the token's company):
 * the strip names that company, never the export scope, so closing never reaches another store in
 * a combined report (manager consistency fixes, 2026-10-09).
 *
 * The query key starts with 'report', so anything that refreshes the report (an approval, a file
 * fetched) refreshes this too.
 */
import { errorText, get, post } from '@/api/client';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { oslo, today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Explain, Status } from '@/ui/page';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { monthLabel } from '../Omsetning';

type Format =
  | 'csv'
  | 'tripletex_csv'
  | 'tripletex_api'
  | 'xlsx'
  | 'pdf'
  | 'poweroffice_csv'
  | 'visma_csv'
  | 'poweroffice_api';
/** GET /admin/pay-month */
export interface PayMonth {
  year: number;
  month: number;
  state: 'open' | 'exported' | 'closed';
  closed: { at: string; by_name: string } | null;
  last_export: {
    format: Format;
    at: string;
    by_name: string;
    lines: number;
    /** the receipt as it was (#265): its file, its own scope, the source version the preview showed (null
     * before 2026-11-04 or for one store's share of a push), and whether today's lines are still those */
    file_name?: string | null;
    scope?: { stores: number; people: number | null } | null;
    source_revision?: string | null;
    same_as_now?: boolean | null;
  } | null;
  /** null when asked with brief: the pay report behind it was not built */
  changed_since_export: boolean | null;
  /** whose lines differ from the last export (#273 OV3-08); null when not known (brief, an export before 2026-11-04, a scoped file) */
  changed_people?: { id: number; name: string }[] | null;
  /** this company's exports of exactly this month, newest first (at most 50) */
  exports?: {
    id: number;
    format: Format;
    exported_at: string;
    lines: number;
    exported_by_name: string;
    /** a short form of the export's checksum, and whether today's lines are the same (#265) */
    revision?: string;
    /** the source version the preview showed, 7 characters; null before 2026-11-04 (#265) */
    source_revision?: string | null;
    same_as_now?: boolean | null;
    /** the name the file was saved under, and the stores and people it covered; null on a send, an old row, or the whole month */
    file_name?: string | null;
    scope?: { stores: number; people: number | null } | null;
  }[];
  events: { action: 'close' | 'reopen'; reason: string | null; at: string; by_name: string }[];
}

export const payMonthKey = (tenant: number | undefined, ym: string) => ['report', 'pay-month', tenant, ym];
/** Every month's export log and its "endret etter eksport" read again, after a file leaves (issue #76: one rule, two callers). */
export const invalidatePayMonth = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: ['report', 'pay-month'] });
const FORMAT: Record<Format, string> = {
  csv: 'CSV-rapport',
  tripletex_csv: 'Tripletex CSV',
  tripletex_api: 'sendt til Tripletex',
  xlsx: 'Excel',
  pdf: 'PDF',
  poweroffice_csv: 'PowerOffice-fil',
  visma_csv: 'Visma-fil',
  poweroffice_api: 'sendt til PowerOffice',
};
/**
 * What an export is and is not (#265): a file is made, not known to be imported; a send was taken
 * in by the payroll system, which is not a payroll run or a payment.
 */
const sent = (f: Format) => f.endsWith('_api');
export const when = (iso: string) => {
  const o = oslo(iso);
  return o ? `${fmtD(o.date)} kl. ${o.time}` : '';
};

/** `enabled` false for a reader without pay.view, who would only be refused (the review bar, #147). */
export function usePayMonth(ym: string, enabled = true, brief = false) {
  const { tenant } = useTenant();
  const [y, m] = ym.split('-').map(Number);
  return useQuery({
    // brief: the state without the pay report behind changed_since_export (null then), for the review bar
    queryKey: brief ? [...payMonthKey(tenant?.id, ym), 'brief'] : payMonthKey(tenant?.id, ym),
    queryFn: () => get<PayMonth>(`/admin/pay-month?year=${y}&month=${m}${brief ? '&brief=1' : ''}`),
    enabled: !!tenant && enabled,
  });
}

/** The payroll system by Innstillinger → Lønn «Lønnssystem», as a sentence names it. */
export const SYSTEM_NAME: Record<string, string> = {
  tripletex: 'Tripletex',
  poweroffice: 'PowerOffice Go',
  visma: 'Visma',
};
/** The file or send a log row is, by its system: a «_api» row was taken in, any other row is a file (#265). */
const systemOf = (f: Format) => SYSTEM_NAME[f.replace(/_(api|csv)$/, '')] ?? '';

/**
 * The delivery row under the statement (V7.4, #281): the month's last actual export event and the
 * company's payroll system with its method. A file made is not an import, and a send taken in is not a
 * payroll run or a payment; a connection on record is called «koblet til», never «fungerer», since
 * nothing here tests it. `onEvent` opens the history; `setupTo` is offered only to whoever may open it.
 */
export function Delivery({
  ym,
  system,
  connected,
  setupTo,
  onEvent,
  company,
}: {
  /** set over several stores: the log is the active company's alone, so the row says whose (#274 R08) */
  company?: string | undefined;
  ym: string;
  system: string;
  connected: boolean;
  setupTo: string | null;
  onEvent: () => void;
}) {
  const s = usePayMonth(ym).data;
  const name = SYSTEM_NAME[system] ?? system;
  const last = s?.last_export;
  const method =
    system !== 'visma' && connected
      ? T('{name}, koblet til: direkte sending eller fil', { name })
      : T('{name}, filimport', { name });
  return (
    <div className="ldeliv" data-testid="delivery">
      <Icon name={last && sent(last.format) ? 'arrowUpRight' : 'download'} />
      <div className="ldelivt">
        {company && <small>{T('Eksport for {name}', { name: company })}</small>}
        {!s ? (
          <b>{T('Henter eksportstatus')}</b>
        ) : last ? (
          <>
            <b>
              {sent(last.format)
                ? T('Tatt imot av {name}', { name: systemOf(last.format) || name })
                : T('Fil laget: {format}', { format: T(FORMAT[last.format]) })}
            </b>
            <span>
              {when(last.at)}, {last.by_name}
              {last.file_name ? `, ${last.file_name}` : ''}.{' '}
              {sent(last.format)
                ? T('Lønnskjøring og utbetaling er ikke bekreftet.')
                : T('Import i lønnssystemet er ikke bekreftet.')}
            </span>
            {/* what this receipt covered, so it is never read as the current one for another scope or version (#265) */}
            <small data-testid="delivery-receipt">
              {[
                last.source_revision ? T('Kildeversjon {v}', { v: last.source_revision }) : '',
                last.scope?.people != null
                  ? T('Gjaldt {n} valgte personer', { n: last.scope.people })
                  : last.scope && last.scope.stores > 1
                    ? T('Gjaldt {n} butikker', { n: last.scope.stores })
                    : T('Gjaldt hele måneden'),
                last.same_as_now === false ? T('timene er endret siden') : '',
              ]
                .filter(Boolean)
                .join(', ')}
            </small>
          </>
        ) : (
          <>
            <b>{T('Ikke eksportert')}</b>
            <span>{method}</span>
          </>
        )}
      </div>
      {last ? (
        <button type="button" className="mlink" onClick={onEvent}>
          {T('Se hendelse')}
          <Icon name="chevronRight" />
        </button>
      ) : (
        setupTo && (
          <Link className="mlink" to={setupTo}>
            {T('Oppsett')}
            <Icon name="chevronRight" />
          </Link>
        )
      )}
    </div>
  );
}

/**
 * The people whose lines moved since the last export, each a link to their month in Kontroll (#273 OV3-08).
 * Nothing when the server could not tell whose; the warning above it still says that something changed.
 */
export function ChangedPeople({ s, ym }: { s: PayMonth | undefined; ym: string }) {
  const people = s?.changed_since_export ? (s.changed_people ?? []) : [];
  if (!people.length) return null;
  return (
    <span className="lchanged" data-testid="changed-people">
      {T('Endret for:')}{' '}
      {people.map((p, i) => (
        <span key={p.id}>
          {i > 0 && ', '}
          <Link to={`/timeliste?vis=alle&mnd=${ym}&ansatt=${p.id}`}>{p.name}</Link>
        </span>
      ))}
    </span>
  );
}

/** "ikke eksportert" or "eksportert 1.10.2026 kl. 08:15 av Kari, Tripletex CSV", for the card's line. */
export const exportState = (s: PayMonth | undefined) =>
  !s
    ? ''
    : s.last_export
      ? T('eksportert {when} av {who}, {format}', {
          when: when(s.last_export.at),
          who: s.last_export.by_name,
          format: T(FORMAT[s.last_export.format]),
        })
      : T('ikke eksportert');

/**
 * Månedslås: one strip under the month card's head, naming the active company. Closing asks first
 * and says what it locks and what it does not; reopening asks why.
 */
export function MonthLock({ ym, to, scopeNames }: { ym: string; to: string; scopeNames: string[] }) {
  const { tenant } = useTenant();
  const can = useCan();
  const qc = useQueryClient();
  const [y, m] = ym.split('-').map(Number);
  const q = usePayMonth(ym);
  const [asking, setAsking] = useState<'close' | 'reopen' | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const s = q.data;
  if (!s) return null;
  const company = tenant?.name ?? '';
  const month = monthLabel(ym);
  const act = async (path: 'close' | 'reopen') => {
    setBusy(true);
    try {
      qc.setQueryData(
        payMonthKey(tenant?.id, ym),
        await post<PayMonth>(`/admin/pay-month/${path}`, { year: y, month: m, reason }),
      );
      toast(path === 'close' ? T('Måneden er lukket') : T('Måneden er åpnet igjen'));
      setAsking(null);
      setReason('');
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const admin = can('timeliste.laase');
  const over = today() > to;
  const closeLabel = T('Lukk {month} for {company}', { month, company });
  const others = scopeNames.filter((n) => n !== company);
  return (
    <div className="llock" data-testid="pay-month">
      <div className="llockrow">
        <div className="llockt">
          <b>
            {T('Månedslås for')} {company}{' '}
            {s.state === 'closed' ? (
              <Status tone="neu">{T('Lukket')}</Status>
            ) : s.state === 'exported' ? (
              <Status tone="ok">{T('Eksportert')}</Status>
            ) : (
              <Status tone="warn">{T('Åpen')}</Status>
            )}
          </b>
          <span>
            {s.closed
              ? T('{month} er lukket {when} av {who}. Godkjente timer i måneden kan ikke endres.', {
                  month,
                  when: when(s.closed.at),
                  who: s.closed.by_name,
                })
              : T('{month} er åpen.', { month })}{' '}
            {T('Låsen gjelder bare denne bedriften.')}
          </span>
        </div>
        {admin && !asking && (
          <span className="llocka">
            {s.state === 'closed' ? (
              <Button small onClick={() => setAsking('reopen')} disabled={busy}>
                {T('Åpne {month} for {company}', { month, company })}
              </Button>
            ) : over ? (
              <Button small onClick={() => setAsking('close')} disabled={busy}>
                {closeLabel}
              </Button>
            ) : (
              <small>{T('Kan lukkes når måneden er over.')}</small>
            )}
          </span>
        )}
      </div>
      {asking === 'close' && (
        <div className="llockask" data-testid="close-confirm">
          <p>
            {T('Du lukker {month} for {company}. Godkjente timer og fravær i denne bedriften låses.', {
              month,
              company,
            })}
          </p>
          {others.length > 0 && (
            <p>
              {T('Eksportutvalget omfatter også {others}. De får ingen endret månedslås.', {
                others: others.join(', '),
              })}
            </p>
          )}
          <span className="llocka">
            <Button tone="pri" small onClick={() => act('close')} disabled={busy}>
              {closeLabel}
            </Button>
            <Button small ghost onClick={() => setAsking(null)} disabled={busy}>
              {T('Avbryt')}
            </Button>
          </span>
        </div>
      )}
      {asking === 'reopen' && (
        <div className="llockask">
          <input
            className="inp"
            aria-label={T('Hvorfor åpnes måneden igjen?')}
            placeholder={T('Hvorfor åpnes måneden igjen?')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <span className="llocka">
            <Button
              tone="pri"
              small
              onClick={() => act('reopen')}
              disabled={busy || reason.trim().length < 3}
            >
              {T('Åpne')}
            </Button>
            <Button small ghost onClick={() => setAsking(null)} disabled={busy}>
              {T('Avbryt')}
            </Button>
          </span>
        </div>
      )}
      <Explain q={T('Hva skjer når måneden lukkes?')}>
        <p>
          {T(
            'Når måneden er lukket, avviser Timefly endringer i godkjente timer og godkjent fravær i den, også nye godkjenninger, til en administrator åpner den igjen med en begrunnelse.',
          )}
        </p>
        <p>
          {T(
            'Låsen gjelder bare {company}. Filene kan fortsatt lastes ned, og lukking og gjenåpning står i eksporthistorikken med hvem og når.',
            { company },
          )}
        </p>
      </Explain>
    </div>
  );
}

/** The month's log for the history panel: every export of exactly this month, and every close and reopen. */
export function MonthHistory({ ym }: { ym: string }) {
  const q = usePayMonth(ym);
  const s = q.data;
  if (!s) return null;
  const exports =
    s.exports ??
    (s.last_export
      ? [{ id: 0, ...s.last_export, exported_at: s.last_export.at, exported_by_name: s.last_export.by_name }]
      : []);
  return (
    <div className="lhist" data-testid="export-history">
      <h3>{T('Eksporter')}</h3>
      {exports.length === 0 ? (
        <p>{T('Ikke eksportert ennå.')}</p>
      ) : (
        <ul>
          {exports.map((x, i) => (
            <li key={x.id || i}>
              <b>
                {T(FORMAT[x.format])}, {x.lines} {x.lines === 1 ? T('linje') : T('linjer')}
              </b>
              <small>
                {when(x.exported_at)}, {x.exported_by_name}
                {x.file_name && `, ${x.file_name}`}
              </small>
              {/* the separate states of one event (V7.4, #281): only what Timefly saw is confirmed */}
              <ol className="lsteps" aria-label={T('Status for eksporten')}>
                <li className="ok">
                  <Icon name="check" />
                  {sent(x.format)
                    ? T('Overført og tatt imot av {name}', { name: systemOf(x.format) || T('lønnssystemet') })
                    : T('Fil laget')}
                </li>
                <li>
                  {sent(x.format)
                    ? T('Lønnskjøring i lønnssystemet: ikke bekreftet')
                    : T('Import i lønnssystemet: ikke bekreftet')}
                </li>
                <li>{T('Lønnsutbetaling: ikke bekreftet av Timefly')}</li>
              </ol>
              <span>
                {x.source_revision && T('Kildeversjon {v}.', { v: x.source_revision })}{' '}
                {x.revision && T('Kontrollsum {v}.', { v: x.revision })}{' '}
                {x.same_as_now === true && T('Samme timer som nå.')}
                {x.same_as_now === false && T('Timene nå er ikke de samme.')}{' '}
                {x.scope && x.scope.stores > 1 && T('Gjelder {n} butikker.', { n: x.scope.stores })}{' '}
                {x.scope?.people != null && T('Gjelder {n} valgte personer.', { n: x.scope.people })}
              </span>
            </li>
          ))}
        </ul>
      )}
      {s.changed_since_export && (
        <p className="lwarn">
          {T('Timene er endret etter siste eksport.')} <ChangedPeople s={s} ym={ym} />
        </p>
      )}
      <h3>{T('Lukking og gjenåpning')}</h3>
      {(s.events ?? []).length === 0 ? (
        <p>{T('Måneden er ikke lukket.')}</p>
      ) : (
        <ul>
          {(s.events ?? []).map((e, i) => (
            <li key={`${e.at}-${i}`}>
              <b>{e.action === 'close' ? T('Lukket') : T('Åpnet igjen')}</b>
              <small>
                {when(e.at)}, {e.by_name}
              </small>
              {e.reason && <span>{e.reason}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
